import Field, { Input } from '@components/Field'
import { useUpdateMe } from '@hooks/useUpdateMe'
import { PROFILE_NAME_MAX_LENGTH, profileNamePatch } from '@lib/profileName'
import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { ContinueButton, NameFields, NameForm } from './ProfileNameForm.styles'

interface ProfileNameFormProps {
	/** Keeps the field ids unique where two forms could share a page. */
	idPrefix: string
	initialFirstName: string
	initialLastName: string
	/** Runs once both names are saved to my profile. */
	onSaved?: () => void
	/** `lg` where Continue sits with a panel's other full-width buttons (the Join page). */
	submitSize?: 'md' | 'lg'
}

/**
 * First and last name, both required and capped at 30, as the apps ask after a
 * Google sign-in that gave none. Saves to my profile; the cached profile then
 * carries the names, which is what closes the step wherever it is shown.
 */
const ProfileNameForm: React.FC<ProfileNameFormProps> = ({
	idPrefix,
	initialFirstName,
	initialLastName,
	onSaved,
	submitSize = 'md',
}) => {
	const [firstName, setFirstName] = useState(initialFirstName)
	const [lastName, setLastName] = useState(initialLastName)
	const updateMe = useUpdateMe()
	const patch = profileNamePatch(firstName, lastName)

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (!patch) return
		updateMe.mutate(patch, {
			onSuccess: () => onSaved?.(),
			onError: error => {
				toast.error('Failed to save your name. Please try again.')
				console.error('Error saving profile name:', error)
			},
		})
	}

	return (
		<NameForm onSubmit={handleSubmit}>
			<NameFields>
				<Field id={`${idPrefix}-first-name`} label="First name">
					<Input
						aria-label="First name"
						type="text"
						value={firstName}
						maxLength={PROFILE_NAME_MAX_LENGTH}
						onChange={e => setFirstName(e.target.value)}
						autoComplete="given-name"
						autoFocus
					/>
				</Field>
				<Field id={`${idPrefix}-last-name`} label="Last name">
					<Input
						aria-label="Last name"
						type="text"
						value={lastName}
						maxLength={PROFILE_NAME_MAX_LENGTH}
						onChange={e => setLastName(e.target.value)}
						autoComplete="family-name"
					/>
				</Field>
			</NameFields>
			<ContinueButton type="submit" size={submitSize} disabled={!patch || updateMe.isPending}>
				{updateMe.isPending ? 'Saving…' : 'Continue'}
			</ContinueButton>
		</NameForm>
	)
}

export default ProfileNameForm
